import { useMemo } from 'react';

const DESIGN_VARIANTS = {
  current: {
    name: 'Current (Production)',
    id: 'current',
    iconSize: 'h-5 w-5',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    iconPadding: 'p-2',
    strokeWidth: 2,

    // Typography (CURRENT - heavy, grey)
    pageTitleWeight: 'font-bold',
    pageTitleSize: 'text-2xl',
    pageTitleColor: 'text-gray-900',
    cardTitleWeight: 'font-semibold',
    cardTitleSize: 'text-lg',
    cardTitleColor: 'text-gray-800',
    tabLabelWeight: 'font-semibold',
    tabLabelSize: 'text-sm',
    tabLabelColor: 'text-gray-700',
    tabActiveColor: 'text-gray-900',
    bodyTextWeight: 'font-normal',
    bodyTextSize: 'text-sm',
    bodyTextColor: 'text-gray-600',
    labelWeight: 'font-medium',
    labelSize: 'text-xs',
    labelColor: 'text-gray-500',
    tableHeaderWeight: 'font-semibold',
    tableHeaderSize: 'text-xs',
    tableHeaderColor: 'text-gray-500',
    statNumberWeight: 'font-bold',
    statNumberSize: 'text-3xl',
    statNumberColor: 'text-gray-900',
    statLabelWeight: 'font-medium',
    statLabelSize: 'text-sm',
    statLabelColor: 'text-gray-600',
    buttonTextWeight: 'font-semibold',
    buttonTextSize: 'text-sm',

    // Legacy
    fontWeight: 'font-semibold',
    fontSize: 'text-sm',
    shadow: 'shadow-sm',
    statusColor: 'text-gray-500',
    statusBadgeBg: 'bg-red-100',
    borderColor: 'border-gray-200',
    tracking: 'tracking-normal',
    rounded: 'rounded-lg',
  },
  modern: {
    name: 'Modern (Clean Icons)',
    id: 'modern',
    iconSize: 'h-8 w-8',
    iconBg: '',
    iconColor: 'text-gray-800',
    iconPadding: '',
    strokeWidth: 2.5,

    // Typography (MODERN - similar to current)
    pageTitleWeight: 'font-bold',
    pageTitleSize: 'text-2xl',
    pageTitleColor: 'text-gray-900',
    cardTitleWeight: 'font-semibold',
    cardTitleSize: 'text-lg',
    cardTitleColor: 'text-gray-800',
    tabLabelWeight: 'font-semibold',
    tabLabelSize: 'text-sm',
    tabLabelColor: 'text-gray-700',
    tabActiveColor: 'text-gray-900',
    bodyTextWeight: 'font-normal',
    bodyTextSize: 'text-sm',
    bodyTextColor: 'text-gray-600',
    labelWeight: 'font-medium',
    labelSize: 'text-xs',
    labelColor: 'text-gray-500',
    tableHeaderWeight: 'font-semibold',
    tableHeaderSize: 'text-xs',
    tableHeaderColor: 'text-gray-500',
    statNumberWeight: 'font-bold',
    statNumberSize: 'text-3xl',
    statNumberColor: 'text-gray-900',
    statLabelWeight: 'font-medium',
    statLabelSize: 'text-sm',
    statLabelColor: 'text-gray-600',
    buttonTextWeight: 'font-semibold',
    buttonTextSize: 'text-sm',

    // Legacy
    fontWeight: 'font-semibold',
    fontSize: 'text-sm',
    shadow: 'shadow-lg',
    statusColor: 'text-red-700',
    statusBadgeBg: '',
    borderColor: 'border-gray-300',
    tracking: 'tracking-normal',
    rounded: 'rounded-lg',
    leftBorder: 'border-l-4',
    leftBorderColor: 'border-red-600',
  },
  ios: {
    name: 'iOS-style (Thin & Light)',
    id: 'ios',
    iconSize: 'h-7 w-7',
    iconBg: '',
    iconColor: 'text-gray-700',
    iconPadding: '',
    strokeWidth: 1.5,

    // Typography (iOS - thin, light)
    pageTitleWeight: 'font-normal',
    pageTitleSize: 'text-2xl',
    pageTitleColor: 'text-gray-800',
    cardTitleWeight: 'font-normal',
    cardTitleSize: 'text-lg',
    cardTitleColor: 'text-gray-700',
    tabLabelWeight: 'font-normal',
    tabLabelSize: 'text-base',
    tabLabelColor: 'text-gray-600',
    tabActiveColor: 'text-gray-800',
    bodyTextWeight: 'font-normal',
    bodyTextSize: 'text-base',
    bodyTextColor: 'text-gray-500',
    labelWeight: 'font-normal',
    labelSize: 'text-sm',
    labelColor: 'text-gray-400',
    tableHeaderWeight: 'font-normal',
    tableHeaderSize: 'text-sm',
    tableHeaderColor: 'text-gray-400',
    statNumberWeight: 'font-normal',
    statNumberSize: 'text-3xl',
    statNumberColor: 'text-gray-800',
    statLabelWeight: 'font-normal',
    statLabelSize: 'text-base',
    statLabelColor: 'text-gray-500',
    buttonTextWeight: 'font-normal',
    buttonTextSize: 'text-base',

    // Legacy
    fontWeight: 'font-normal',
    fontSize: 'text-base',
    shadow: 'shadow',
    statusColor: 'text-red-600',
    statusBadgeBg: '',
    borderColor: 'border-gray-200',
    tracking: 'tracking-tight',
    rounded: 'rounded-xl',
  },
  rich: {
    name: 'Rich Colors (Dark)',
    id: 'rich',
    iconSize: 'h-8 w-8',
    iconBg: '',
    iconColor: 'text-gray-900',
    iconPadding: '',
    strokeWidth: 2.5,

    // Typography (RICH - bold, dark)
    pageTitleWeight: 'font-bold',
    pageTitleSize: 'text-2xl',
    pageTitleColor: 'text-gray-900',
    cardTitleWeight: 'font-bold',
    cardTitleSize: 'text-lg',
    cardTitleColor: 'text-gray-900',
    tabLabelWeight: 'font-bold',
    tabLabelSize: 'text-base',
    tabLabelColor: 'text-gray-800',
    tabActiveColor: 'text-gray-900',
    bodyTextWeight: 'font-medium',
    bodyTextSize: 'text-base',
    bodyTextColor: 'text-gray-700',
    labelWeight: 'font-semibold',
    labelSize: 'text-xs',
    labelColor: 'text-gray-600',
    tableHeaderWeight: 'font-bold',
    tableHeaderSize: 'text-xs',
    tableHeaderColor: 'text-gray-700',
    statNumberWeight: 'font-bold',
    statNumberSize: 'text-3xl',
    statNumberColor: 'text-gray-900',
    statLabelWeight: 'font-semibold',
    statLabelSize: 'text-base',
    statLabelColor: 'text-gray-700',
    buttonTextWeight: 'font-bold',
    buttonTextSize: 'text-base',

    // Legacy
    fontWeight: 'font-bold',
    fontSize: 'text-base',
    shadow: 'shadow-xl',
    statusColor: 'text-red-800',
    statusBadgeBg: 'bg-red-900',
    statusBadgeText: 'text-white',
    borderColor: 'border-gray-300',
    bgColor: 'bg-gray-50',
    tracking: 'tracking-normal',
    rounded: 'rounded-lg',
  },
  refined: {
    name: 'Refined (Thin & Dark Text) ⭐',
    id: 'refined',

    // ========================================
    // ICONS (Slightly smaller & less bold)
    // ========================================
    iconSize: 'h-7 w-7',           // 28px (reduced from MODERN's 32px)
    iconBg: '',                    // No background (clean aesthetic)
    iconColor: 'text-gray-900',    // Very dark (darker than MODERN's gray-800)
    iconPadding: '',
    strokeWidth: 2,                // Balanced (less than MODERN's 2.5, more than iOS's 1.5)

    // ========================================
    // TYPOGRAPHY: "THIN & DARK" PHILOSOPHY
    // ========================================

    // Page-level titles (Equipment Register, Maintenance Jobs)
    pageTitleWeight: 'font-semibold',     // 600 (CURRENT uses font-bold 700)
    pageTitleSize: 'text-2xl',            // 24px
    pageTitleColor: 'text-gray-950',      // Almost black (CURRENT uses gray-900)

    // Card/Section titles (QuickStats headers, card titles)
    cardTitleWeight: 'font-medium',       // 500 (CURRENT uses font-semibold 600)
    cardTitleSize: 'text-lg',             // 18px
    cardTitleColor: 'text-gray-950',      // Almost black

    // Section headers (smaller than card titles)
    sectionHeaderWeight: 'font-medium',   // 500 (CURRENT uses font-semibold 600)
    sectionHeaderSize: 'text-base',       // 16px
    sectionHeaderColor: 'text-gray-900',  // Dark grey

    // Tab labels (FilterBar tabs, navigation tabs)
    tabLabelWeight: 'font-medium',        // 500 (CURRENT uses font-semibold 600)
    tabLabelSize: 'text-sm',              // 14px
    tabLabelColor: 'text-gray-900',       // Dark grey (CURRENT uses gray-700)
    tabActiveColor: 'text-gray-950',      // Almost black when active

    // Body text (descriptions, paragraphs)
    bodyTextWeight: 'font-normal',        // 400 (unchanged)
    bodyTextSize: 'text-sm',              // 14px
    bodyTextColor: 'text-gray-800',       // Darker (CURRENT uses gray-600)

    // Labels (form labels, small headings)
    labelWeight: 'font-normal',           // 400 (CURRENT uses font-medium 500)
    labelSize: 'text-xs',                 // 12px
    labelColor: 'text-gray-700',          // Medium-dark (CURRENT uses gray-500)

    // Table headers
    tableHeaderWeight: 'font-medium',     // 500 (CURRENT uses font-semibold 600)
    tableHeaderSize: 'text-xs',           // 12px
    tableHeaderColor: 'text-gray-900',    // Very dark (CURRENT uses gray-500!)

    // Stat numbers (QuickStats big numbers)
    statNumberWeight: 'font-semibold',    // 600 (CURRENT uses font-bold 700)
    statNumberSize: 'text-sm',            // 14px (user requested 2px smaller)
    statNumberColor: 'text-gray-950',     // Almost black

    // Stat labels (QuickStats labels below numbers)
    statLabelWeight: 'font-normal',       // 400 (CURRENT uses font-medium 500)
    statLabelSize: 'text-sm',             // 14px
    statLabelColor: 'text-gray-800',      // Darker (CURRENT uses gray-600)

    // Button text
    buttonTextWeight: 'font-medium',      // 500 (CURRENT uses font-semibold 600)
    buttonTextSize: 'text-sm',            // 14px

    // Small descriptions (help text, hints)
    descriptionWeight: 'font-normal',     // 400
    descriptionSize: 'text-xs',           // 12px
    descriptionColor: 'text-gray-700',    // Medium-dark (CURRENT uses gray-500)

    // ========================================
    // STATUS COLORS (Richer/Darker)
    // ========================================
    statusColor: 'text-red-800',          // Richer than MODERN's red-700
    statusColors: {
      overdue: 'text-red-800',
      warning: 'text-amber-800',
      compliant: 'text-green-800',
      info: 'text-blue-800',
    },
    statusBadgeBg: '',

    // ========================================
    // BORDERS & ACCENTS
    // ========================================
    leftBorder: 'border-l-4',
    leftBorderColor: 'border-red-600',
    borderColors: {
      overdue: 'border-red-600',
      warning: 'border-amber-500',
      compliant: 'border-green-600',
      info: 'border-blue-600',
    },
    borderColor: 'border-gray-200',

    // ========================================
    // SHADOWS, BACKGROUNDS, SPACING
    // ========================================
    shadow: 'shadow-lg',
    cardBg: 'bg-white',
    cardBorder: 'border border-gray-200',
    tracking: 'tracking-normal',
    rounded: 'rounded-lg',

    // Legacy properties for compatibility
    fontWeight: 'font-medium',
    fontSize: 'text-sm',
    textColor: 'text-gray-950',
  },
  seaready: {
    name: 'FleetSkipper (Complete Website Style) 🚢',
    id: 'seaready',

    // ========================================
    // COLORS - Complete Website Palette
    // ========================================

    // Primary Brand Colors
    primaryOrange: 'bg-orange-600',        // #c65d00 - Main CTAs
    primaryCyan: 'bg-cyan-600',            // #0891B2 - Secondary CTAs
    darkNavy: 'text-slate-900',            // #0E1A2B - Headings

    // Background Colors
    pageBg: 'bg-white',
    cardBg: 'bg-white',
    footerBg: 'bg-slate-800',              // #121C27

    // Text Colors
    headingColor: 'text-slate-900',        // Dark navy for all headings
    bodyTextColor: 'text-gray-500',        // #6B7280 - Body text
    mutedText: 'text-gray-600',            // #4B535D - Tertiary text
    subtleText: 'text-gray-400',           // #C4C4C4 - Very subtle

    // White Alpha Variations (for dark backgrounds)
    whiteAlpha: {
      95: 'text-white/95',
      90: 'text-white/90',
      80: 'text-white/80',
      70: 'text-white/70',
      60: 'text-white/60',
      50: 'text-white/50',
      40: 'text-white/40',
      10: 'bg-white/10',
      '05': 'bg-white/5',
    },

    // ========================================
    // TYPOGRAPHY - Complete Hierarchy
    // ========================================

    // Font Family
    fontFamily: 'Inter, system-ui, sans-serif',

    // H1 - Hero Headings (60px, bold, tight tracking)
    h1Weight: 'font-bold',
    h1Size: 'text-6xl',                    // 60px
    h1Color: 'text-white',                 // On dark hero sections
    h1Tracking: 'tracking-tight',          // -1.5px
    h1Leading: 'leading-tight',            // 1.0

    // H2 - Section Headings (36px, bold, navy)
    h2Weight: 'font-bold',
    h2Size: 'text-4xl',                    // 36px
    h2Color: 'text-slate-900',             // Dark navy
    h2Leading: 'leading-snug',             // 1.11

    // H3 - Card/Subsection Headings (20px, bold, navy)
    h3Weight: 'font-bold',
    h3Size: 'text-xl',                     // 20px
    h3Color: 'text-slate-900',             // Dark navy
    h3Leading: 'leading-relaxed',          // 1.4

    // Page-level titles (Equipment Register, Maintenance Jobs) - COMPACT
    pageTitleWeight: 'font-bold',
    pageTitleSize: 'text-2xl',             // 24px (was text-3xl = 30px) - more compact
    pageTitleColor: 'text-slate-900',

    // Card/Section titles (for notification cards: small, dark, medium weight)
    cardTitleWeight: 'font-medium',        // 500 (was font-semibold = 600) - lighter for message emphasis
    cardTitleSize: 'text-sm',              // 14px - SMALLER than message
    cardTitleColor: 'text-slate-900',      // Dark navy - NOT colored

    // Section headers
    sectionHeaderWeight: 'font-semibold',
    sectionHeaderSize: 'text-lg',          // 18px
    sectionHeaderColor: 'text-slate-800',

    // Tab labels - FIXED: visible on light backgrounds
    tabLabelWeight: 'font-normal',         // Regular weight
    tabLabelSize: 'text-base',             // 16px
    tabLabelColor: 'text-gray-600',        // Was text-white/70 - INVISIBLE on light!
    tabActiveColor: 'text-blue-600',       // Was text-white/90 - INVISIBLE on light!
    tabActiveWeight: 'font-medium',        // Slightly heavier when active
    tabInactiveHover: 'hover:text-gray-900',

    // Body text - Large (intro/hero)
    bodyXlWeight: 'font-normal',
    bodyXlSize: 'text-xl',                 // 20px
    bodyXlColor: 'text-white/95',          // On dark
    bodyXlLeading: 'leading-relaxed',      // 1.6

    // Body text - Regular
    bodyTextWeight: 'font-normal',
    bodyTextSize: 'text-lg',               // 18px
    bodyTextLeading: 'leading-relaxed',    // 1.78

    // Body text - Small
    bodySmWeight: 'font-normal',
    bodySmSize: 'text-base',               // 16px
    bodySmColor: 'text-gray-600',
    bodySmLeading: 'leading-relaxed',      // 1.625

    // Labels
    labelWeight: 'font-normal',
    labelSize: 'text-sm',                  // 14px
    labelColor: 'text-gray-500',

    // Base text - COMPACT (for lists, body copy)
    baseTextSize: 'text-sm',               // 14px (was text-base = 16px) - more compact
    baseTextWeight: 'font-normal',
    baseTextColor: 'text-slate-700',
    baseLineHeight: 'leading-relaxed',

    // Small text (captions, hints)
    smallTextSize: 'text-xs',              // 12px
    smallTextWeight: 'font-normal',
    smallTextColor: 'text-slate-600',

    // Table styling - COMPACT
    tableHeaderWeight: 'font-semibold',
    tableHeaderSize: 'text-sm',            // 14px - consistent with compact sizing
    tableHeaderColor: 'text-slate-900',
    tableHeaderBg: 'bg-slate-50',
    tableRowHeight: 'h-14',                // 56px (was h-16 = 64px) - more compact
    tableCellPadding: 'py-3 px-4',         // 12px/16px (was py-4 px-6) - tighter
    tableTextSize: 'text-sm',              // 14px (was text-base = 16px) - more compact
    tableTextWeight: 'font-normal',
    tableTextColor: 'text-slate-700',

    // Equipment titles - primary identifiers stand out
    equipmentTitleWeight: 'font-medium',   // 500 weight - heavier than body text

    // Stat numbers (QuickStats, metrics)
    statNumberWeight: 'font-bold',
    statNumberSize: 'text-2xl',            // 24px - dashboard appropriate
    statNumberColor: 'text-slate-900',     // Dark navy - readable

    // Stat labels
    statLabelWeight: 'font-normal',
    statLabelSize: 'text-sm',
    statLabelColor: 'text-gray-500',

    // Message styling (for contextual notification cards - LARGER than title)
    messageWeight: 'font-medium',             // 500 - medium weight
    messageSize: 'text-base',                 // 16px - LARGER than title
    messageColorOverdue: 'text-red-600',      // Red for overdue
    messageColorWarning: 'text-amber-600',    // Amber for warning
    messageColorSafe: 'text-green-600',       // Green for safe

    // Button text
    buttonTextWeight: 'font-semibold',
    buttonTextSize: 'text-sm',             // 14px for small, text-base/lg for large

    // Section badge/overline
    badgeWeight: 'font-normal',
    badgeSize: 'text-base',
    badgeColor: 'text-cyan-600',

    // ========================================
    // ICONS - COMPACT (better proportioned to text)
    // ========================================
    iconSize: 'h-7 w-7',                   // 28px (was h-8 w-8 = 32px) - better text balance
    iconColor: 'text-cyan-600',            // Keep cyan for branding
    iconBg: '',                            // No background
    iconPadding: '',
    strokeWidth: 2,

    // Small icons (nav, lists)
    iconSmallSize: 'h-6 w-6',              // 24px
    iconSmallColor: 'text-white/70',       // On dark backgrounds

    // ========================================
    // BUTTONS - Complete System
    // ========================================

    // Primary CTA Button (Orange)
    primaryButtonBg: 'bg-orange-600',
    primaryButtonHover: 'hover:bg-orange-700',
    primaryButtonText: 'text-white',
    primaryButtonRounded: 'rounded-full',  // Pill shape
    primaryButtonPadding: 'px-4 py-2',     // Small variant
    primaryButtonPaddingLg: 'px-8 py-4',   // Large variant
    primaryButtonShadow: 'shadow-sm',
    primaryButtonShadowHover: 'hover:shadow-md',

    // Secondary Button (Glass/Outline on dark)
    secondaryButtonBg: 'bg-white/10',
    secondaryButtonHover: 'hover:bg-white/20',
    secondaryButtonText: 'text-white',
    secondaryButtonRing: 'ring-2 ring-white',
    secondaryButtonRounded: 'rounded-full',
    secondaryButtonPadding: 'px-8 py-4',

    // Tertiary Button (Cyan)
    tertiaryButtonBg: 'bg-cyan-600',
    tertiaryButtonHover: 'hover:bg-cyan-700',
    tertiaryButtonText: 'text-white',
    tertiaryButtonRounded: 'rounded-full',
    tertiaryButtonPadding: 'px-8 py-4',
    tertiaryButtonShadow: 'shadow-lg',

    // Nav Link Button
    navLinkText: 'text-white/70',
    navLinkHover: 'hover:text-white',
    navLinkRounded: 'rounded-md',
    navLinkPadding: 'px-2.5 py-2.5',

    // ========================================
    // CARDS & CONTAINERS (Refined-style compact)
    // ========================================
    cardBorder: 'ring-1 ring-gray-100',    // Ring border (not solid)
    cardRounded: 'rounded-lg',             // 8px (was rounded-2xl = 16px) - more compact
    cardPadding: 'p-6',                    // 24px (was p-10 = 40px) - more compact
    cardShadow: 'shadow-md',
    cardShadowHover: 'hover:shadow-lg',

    // Card Layout (HORIZONTAL - icon on RIGHT like Refined)
    cardLayout: 'flex items-center justify-between',  // Horizontal layout
    cardIconMargin: '',                    // Not needed in horizontal layout
    cardTitleMargin: '',                   // Not needed in horizontal layout

    // Grid Layout
    gridGap: 'gap-6',                      // 24px (was gap-8 = 32px) - more compact
    gridCols: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',

    // ========================================
    // SPACING SYSTEM
    // ========================================
    sectionPaddingY: 'py-16 md:py-24',     // 64px-96px vertical
    sectionPaddingX: 'px-8',               // 32px horizontal
    containerMaxWidth: 'max-w-7xl',        // 1280px
    contentMaxWidth: 'max-w-3xl',          // 768px for centered text

    // Common Spacing
    spacing: {
      xs: 'space-y-1.5',                   // 6px
      sm: 'space-y-2',                     // 8px
      md: 'space-y-3',                     // 12px
      base: 'space-y-4',                   // 16px
      lg: 'space-y-6',                     // 24px
      xl: 'space-y-8',                     // 32px
      '2xl': 'space-y-10',                 // 40px
      '3xl': 'space-y-12',                 // 48px
    },

    // ========================================
    // BORDERS & ACCENTS
    // ========================================
    borderColor: 'border-gray-200',
    borderSubtle: 'border-gray-100',
    borderRadius: {
      sm: 'rounded-md',                    // 6px
      md: 'rounded-xl',                    // 12px
      lg: 'rounded-2xl',                   // 16px
      full: 'rounded-full',                // 9999px
    },

    // Status Colors (for badges, not card numbers)
    statusColors: {
      success: 'text-green-600',
      warning: 'text-amber-600',
      error: 'text-red-600',
      info: 'text-cyan-600',
    },

    // ========================================
    // SHADOWS & ELEVATION
    // ========================================
    shadow: 'shadow-md',                   // Default card shadow
    shadowSm: 'shadow-sm',                 // Buttons
    shadowLg: 'shadow-lg',                 // Elevated elements
    shadowXl: 'shadow-xl',                 // Hover states
    shadow2xl: 'shadow-2xl',               // Dramatic emphasis

    // Ring Effects (for focus/secondary buttons)
    ring: 'ring-2 ring-white',
    ringSubtle: 'ring-1 ring-white/10',

    // ========================================
    // TRANSITIONS & ANIMATIONS
    // ========================================
    transitionFast: 'transition-colors duration-150 ease-in-out',
    transitionNormal: 'transition-all duration-300 ease-in-out',

    // Hover Effects
    hoverLift: 'hover:-translate-y-1',
    hoverShadow: 'hover:shadow-xl',
    hoverEffect: 'hover:-translate-y-1 hover:shadow-xl transition-all duration-300',

    // ========================================
    // NAVIGATION & HEADER
    // ========================================
    navHeight: 'h-26',                     // 104px
    navPadding: 'py-6 px-8',
    navBg: 'bg-transparent',               // Or bg-gray-900/95 for solid
    navBackdrop: 'backdrop-blur-sm',       // If using transparency

    // ========================================
    // FOOTER
    // ========================================
    footerText: 'text-white',
    footerLink: 'text-gray-400',
    footerLinkHover: 'hover:text-white',
    footerPadding: 'py-16 md:py-24',

    // ========================================
    // SPECIAL COMPONENTS
    // ========================================

    // Social Media Icons
    socialIconBg: 'bg-slate-700',          // #353E47
    socialIconHover: 'hover:bg-slate-700',
    socialIconSize: 'w-10 h-10',
    socialIconRounded: 'rounded-full',

    // List with Icons
    listStyle: 'list-none space-y-4',
    listItemLayout: 'flex items-start gap-3',
    listIconSize: 'w-6 h-6 mt-0.5',

    // FAQ/Accordion
    faqQuestionWeight: 'font-bold',
    faqQuestionSize: 'text-lg',
    faqQuestionColor: 'text-slate-900',
    faqAnswerWeight: 'font-normal',
    faqAnswerSize: 'text-base',
    faqAnswerColor: 'text-gray-600',

    // Section Badge
    badgeBg: '',                           // Transparent
    badgeText: 'text-cyan-600',
    badgeMargin: 'mb-6',

    // ========================================
    // Legacy Properties (for compatibility)
    // ========================================
    fontWeight: 'font-normal',
    fontSize: 'text-base',
    textColor: 'text-slate-900',
    rounded: 'rounded-2xl',
    tracking: 'tracking-normal',
  },

  // ========================================
  // SEAREADY V2 - REFINED & COMPACT
  // All refinements from briefs applied
  // ========================================
  seareadyv2: {
    name: 'FleetSkipper v2 (Refined - Compact) 🚢✨',
    id: 'seareadyv2',

    // ========================================
    // COLORS - Complete Website Palette (same as seaready)
    // ========================================
    primaryOrange: 'bg-orange-600',
    primaryCyan: 'bg-cyan-600',
    darkNavy: 'text-slate-900',
    pageBg: 'bg-white',
    cardBg: 'bg-white',
    footerBg: 'bg-slate-800',
    headingColor: 'text-slate-900',
    bodyTextColor: 'text-gray-500',
    mutedText: 'text-gray-600',
    subtleText: 'text-gray-400',
    whiteAlpha: {
      95: 'text-white/95',
      90: 'text-white/90',
      80: 'text-white/80',
      70: 'text-white/70',
      60: 'text-white/60',
      50: 'text-white/50',
      40: 'text-white/40',
      10: 'bg-white/10',
      '05': 'bg-white/5',
    },

    // ========================================
    // TYPOGRAPHY - Same as seaready
    // ========================================
    fontFamily: 'Inter, system-ui, sans-serif',
    h1Weight: 'font-bold',
    h1Size: 'text-6xl',
    h1Color: 'text-white',
    h1Tracking: 'tracking-tight',
    h1Leading: 'leading-tight',
    h2Weight: 'font-bold',
    h2Size: 'text-4xl',
    h2Color: 'text-slate-900',
    h2Leading: 'leading-snug',
    h3Weight: 'font-bold',
    h3Size: 'text-xl',
    h3Color: 'text-slate-900',
    h3Leading: 'leading-relaxed',
    pageTitleWeight: 'font-bold',
    pageTitleSize: 'text-2xl',
    pageTitleColor: 'text-slate-900',
    cardTitleWeight: 'font-medium',
    cardTitleSize: 'text-sm',
    cardTitleColor: 'text-slate-900',
    sectionHeaderWeight: 'font-semibold',
    sectionHeaderSize: 'text-lg',
    sectionHeaderColor: 'text-slate-800',

    // Tab labels - VISIBLE on light backgrounds
    tabLabelWeight: 'font-normal',
    tabLabelSize: 'text-base',
    tabLabelColor: 'text-gray-600',
    tabActiveColor: 'text-blue-600',
    tabActiveWeight: 'font-medium',
    tabInactiveHover: 'hover:text-gray-900',

    // Body text
    bodyXlWeight: 'font-normal',
    bodyXlSize: 'text-xl',
    bodyXlColor: 'text-white/95',
    bodyXlLeading: 'leading-relaxed',
    bodyTextWeight: 'font-normal',
    bodyTextSize: 'text-lg',
    bodyTextLeading: 'leading-relaxed',
    bodySmWeight: 'font-normal',
    bodySmSize: 'text-base',
    bodySmColor: 'text-gray-600',
    bodySmLeading: 'leading-relaxed',
    labelWeight: 'font-normal',
    labelSize: 'text-sm',
    labelColor: 'text-gray-500',
    baseTextSize: 'text-sm',
    baseTextWeight: 'font-normal',
    baseTextColor: 'text-slate-700',
    baseLineHeight: 'leading-relaxed',
    smallTextSize: 'text-xs',
    smallTextWeight: 'font-normal',
    smallTextColor: 'text-slate-600',

    // Table styling
    tableHeaderWeight: 'font-semibold',
    tableHeaderSize: 'text-sm',
    tableHeaderColor: 'text-slate-900',
    tableHeaderBg: 'bg-slate-50',
    tableRowHeight: 'h-14',
    tableCellPadding: 'py-3 px-4',
    tableTextSize: 'text-sm',
    tableTextWeight: 'font-normal',
    tableTextColor: 'text-slate-700',

    // ========================================
    // EQUIPMENT TITLES - FINAL POLISH
    // Primary identifiers (equipment names, crew names) - heavier than body text
    // ========================================
    equipmentTitleWeight: 'font-medium',       // 500 weight - stands out as primary identifier

    // Stat numbers
    statNumberWeight: 'font-bold',
    statNumberSize: 'text-2xl',
    statNumberColor: 'text-slate-900',
    statLabelWeight: 'font-normal',
    statLabelSize: 'text-sm',
    statLabelColor: 'text-gray-500',

    // ========================================
    // MESSAGE TEXT - REFINED (14px instead of 16px)
    // ========================================
    messageWeight: 'font-medium',
    messageSize: 'text-sm',                    // 14px (was text-base = 16px) - REFINED
    messageColorOverdue: 'text-red-600',
    messageColorWarning: 'text-amber-600',
    messageColorSafe: 'text-green-600',

    // Button text
    buttonTextWeight: 'font-semibold',
    buttonTextSize: 'text-sm',

    // ========================================
    // STATUS BADGES - DRILLS MODULE STYLE
    // ========================================
    badgeSize: 'text-xs',                      // 12px (was text-sm = 14px)
    badgePadding: 'py-0.5 px-2',               // 2px/8px (was py-1 px-2 = 4px/8px)
    badgeRounded: 'rounded-full',              // 9999px (was rounded-2xl = 16px)
    badgeWeight: 'font-medium',                // 500 (was font-semibold = 600)
    badgeIconSize: 'h-3 w-3',                  // 12px icon
    badgeIconMargin: 'mr-1',                   // 4px space after icon
    badgeDisplay: 'inline-flex items-center',  // Flex for icon + text

    // Badge colors
    badgeOverdueBg: 'bg-red-100',
    badgeOverdueText: 'text-red-600',
    badgeWarningBg: 'bg-yellow-100',
    badgeWarningText: 'text-amber-600',
    badgeSafeBg: 'bg-green-100',
    badgeSafeText: 'text-green-600',

    badgeColor: 'text-cyan-600',

    // ========================================
    // ICONS - COMPACT
    // ========================================
    iconSize: 'h-7 w-7',
    iconColor: 'text-cyan-600',
    iconBg: '',
    iconPadding: '',
    strokeWidth: 2,
    iconSmallSize: 'h-6 w-6',
    iconSmallColor: 'text-white/70',

    // ========================================
    // BUTTONS
    // ========================================
    primaryButtonBg: 'bg-orange-600',
    primaryButtonHover: 'hover:bg-orange-700',
    primaryButtonText: 'text-white',
    primaryButtonRounded: 'rounded-full',
    primaryButtonPadding: 'px-4 py-2',
    primaryButtonPaddingLg: 'px-8 py-4',
    primaryButtonShadow: 'shadow-sm',
    primaryButtonShadowHover: 'hover:shadow-md',
    secondaryButtonBg: 'bg-white/10',
    secondaryButtonHover: 'hover:bg-white/20',
    secondaryButtonText: 'text-white',
    secondaryButtonRing: 'ring-2 ring-white',
    secondaryButtonRounded: 'rounded-full',
    secondaryButtonPadding: 'px-8 py-4',
    tertiaryButtonBg: 'bg-cyan-600',
    tertiaryButtonHover: 'hover:bg-cyan-700',
    tertiaryButtonText: 'text-white',
    tertiaryButtonRounded: 'rounded-full',
    tertiaryButtonPadding: 'px-8 py-4',
    tertiaryButtonShadow: 'shadow-lg',
    navLinkText: 'text-white/70',
    navLinkHover: 'hover:text-white',
    navLinkRounded: 'rounded-md',
    navLinkPadding: 'px-2.5 py-2.5',

    // ========================================
    // CARDS & CONTAINERS
    // ========================================
    cardBorder: 'ring-1 ring-gray-100',
    cardRounded: 'rounded-lg',
    cardPadding: 'p-6',
    cardShadow: 'shadow-md',
    cardShadowHover: 'hover:shadow-lg',
    cardLayout: 'flex items-center justify-between',
    cardIconMargin: '',
    cardTitleMargin: '',
    gridGap: 'gap-6',
    gridCols: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',

    // ========================================
    // SPACING
    // ========================================
    sectionPaddingY: 'py-16 md:py-24',
    sectionPaddingX: 'px-8',
    containerMaxWidth: 'max-w-7xl',
    contentMaxWidth: 'max-w-3xl',
    spacing: {
      xs: 'space-y-1.5',
      sm: 'space-y-2',
      md: 'space-y-3',
      base: 'space-y-4',
      lg: 'space-y-6',
      xl: 'space-y-8',
      '2xl': 'space-y-10',
      '3xl': 'space-y-12',
    },

    // ========================================
    // BORDERS & ACCENTS
    // ========================================
    borderColor: 'border-gray-200',
    borderSubtle: 'border-gray-100',
    borderRadius: {
      sm: 'rounded-md',
      md: 'rounded-xl',
      lg: 'rounded-2xl',
      full: 'rounded-full',
    },
    statusColors: {
      success: 'text-green-600',
      warning: 'text-amber-600',
      error: 'text-red-600',
      info: 'text-cyan-600',
    },

    // ========================================
    // SHADOWS & EFFECTS
    // ========================================
    shadow: 'shadow-md',
    shadowSm: 'shadow-sm',
    shadowLg: 'shadow-lg',
    shadowXl: 'shadow-xl',
    shadow2xl: 'shadow-2xl',
    ring: 'ring-2 ring-white',
    ringSubtle: 'ring-1 ring-white/10',
    transitionFast: 'transition-colors duration-150 ease-in-out',
    transitionNormal: 'transition-all duration-300 ease-in-out',
    hoverLift: 'hover:-translate-y-1',
    hoverShadow: 'hover:shadow-xl',
    hoverEffect: 'hover:-translate-y-1 hover:shadow-xl transition-all duration-300',

    // ========================================
    // NAVIGATION & FOOTER
    // ========================================
    navHeight: 'h-26',
    navPadding: 'py-6 px-8',
    navBg: 'bg-transparent',
    navBackdrop: 'backdrop-blur-sm',
    footerText: 'text-white',
    footerLink: 'text-gray-400',
    footerLinkHover: 'hover:text-white',
    footerPadding: 'py-16 md:py-24',

    // ========================================
    // SPECIAL COMPONENTS
    // ========================================
    socialIconBg: 'bg-slate-700',
    socialIconHover: 'hover:bg-slate-700',
    socialIconSize: 'w-10 h-10',
    socialIconRounded: 'rounded-full',
    listStyle: 'list-none space-y-4',
    listItemLayout: 'flex items-start gap-3',
    listIconSize: 'w-6 h-6 mt-0.5',
    faqQuestionWeight: 'font-bold',
    faqQuestionSize: 'text-lg',
    faqQuestionColor: 'text-slate-900',
    faqAnswerWeight: 'font-normal',
    faqAnswerSize: 'text-base',
    faqAnswerColor: 'text-gray-600',
    badgeBg: '',
    badgeText: 'text-cyan-600',
    badgeMargin: 'mb-6',

    // ========================================
    // Legacy Properties
    // ========================================
    fontWeight: 'font-normal',
    fontSize: 'text-base',
    textColor: 'text-slate-900',
    rounded: 'rounded-2xl',
    tracking: 'tracking-normal',
  },
};

export const useDesignVariants = (variantId) => {
  return useMemo(() => {
    return DESIGN_VARIANTS[variantId] || DESIGN_VARIANTS.current;
  }, [variantId]);
};

export const getVariantNames = () => {
  return Object.keys(DESIGN_VARIANTS).map(key => ({
    id: key,
    name: DESIGN_VARIANTS[key].name,
  }));
};

export default useDesignVariants;
